import { Component, Input, OnInit, OnDestroy, ChangeDetectorRef, ElementRef, Renderer2 } from '@angular/core';
import { Subscription } from 'rxjs';
import { FormBuilder } from '@angular/forms';
import { MatSnackBar } from '@angular/material/snack-bar';
import { Router, ActivatedRoute } from '@angular/router';
import { DomSanitizer } from '@angular/platform-browser';
import { StudioTaxService, StudioTaxMode } from 'src/app/services/studio-tax.service';
import { DialogService } from 'src/app/services/dialog.service';
import { EntityRESTService } from 'src/app/services/entity-rest.service';
import { UtilityService } from 'src/app/services/utility.service';
import { FieldsService } from 'src/app/services/fields.service';
import { DrupalRESTService } from 'src/app/services/drupal-rest.service';
import { TaxonomyService } from 'src/app/services/taxonomy.service';
import { AuthRESTService } from 'src/app/services/auth-rest.service';
import { AuthService } from 'src/app/services/auth.service';
import { MenuRESTService } from 'src/app/services/menu-rest.service';
import { RouteEventsService } from 'src/app/route-events.service';
import { PermissionsService } from 'src/app/services/permissions.service';
import { ConfigPagesService } from 'src/app/services/config-pages.service';
import { PackagesService } from 'src/app/services/packages.service';
import { UserService } from 'src/app/services/user.service';
import { RefreshService } from 'src/app/services/refresh.service';
import { StudioConfigService } from 'src/app/services/studio-config.service';
import { ComponentRefreshService } from 'src/app/services/component-refresh.service';
import { TranslateService } from '@ngx-translate/core';
import { MatTableDataSource } from '@angular/material/table';
import { ViewTableComponent } from '../../base/view-table/view-table.component';
import { ViewPaymentsFromEnrollmentComponent } from '../../forms/view-payments-from-enrollment/view-payments-from-enrollment.component';
import { StudentDashboardWrapperComponent } from '../../student/student-dashboard-wrapper/student-dashboard-wrapper.component';
import { ComponentType } from '@angular/cdk/overlay';

@Component({
  selector: 'app-enrollment-view-payment-view',
  templateUrl: './enrollment-view-payment-view.component.html',
  styleUrls: ['./enrollment-view-payment-view.component.css']
})
export class EnrollmentViewPaymentViewComponent extends ViewTableComponent implements OnInit, OnDestroy {

  @Input() override enrollmentID?: any;

  override displayedColumns: string[] = [
    'field_payment_id',
    'field_customer_name',
    'field_date_and_time',
    'field_gross_tuition',
    'AllScheduledPaymentInstructors',
    'edit',
  ];

  StudentDashboardWrapperComponent = StudentDashboardWrapperComponent;
  ViewPaymentsFromEnrollmentComponent = ViewPaymentsFromEnrollmentComponent;

  hasTaxData = false;

  /** Studio tax mode; drives the legacy-row fallback total. */
  private taxMode: StudioTaxMode | null = null;

  constructor(
    refreshService: RefreshService,
    dialogService: DialogService,
    entityRESTService: EntityRESTService,
    utilityService: UtilityService,
    fieldsService: FieldsService,
    drupalRESTService: DrupalRESTService,
    taxonomyService: TaxonomyService,
    router: Router,
    activatedRoute: ActivatedRoute,
    authRESTService: AuthRESTService,
    authService: AuthService,
    menuRESTService: MenuRESTService,
    formBuilder: FormBuilder,
    routeEventsService: RouteEventsService,
    permissionsService: PermissionsService,
    renderer2: Renderer2,
    configPages: ConfigPagesService,
    packagesService: PackagesService,
    snackBar: MatSnackBar,
    userRESTService: UserService,
    el: ElementRef,
    changeDetectorRef: ChangeDetectorRef,
    sanitizer: DomSanitizer,
    studioConfigService: StudioConfigService,
    componentRefreshService: ComponentRefreshService,
    translateService: TranslateService,
    private studioTaxService: StudioTaxService
  ) {
    super(
      refreshService,
      dialogService,
      entityRESTService,
      utilityService,
      fieldsService,
      drupalRESTService,
      taxonomyService,
      router,
      activatedRoute,
      authRESTService,
      authService,
      menuRESTService,
      formBuilder,
      routeEventsService,
      permissionsService,
      renderer2,
      configPages,
      packagesService,
      snackBar,
      userRESTService,
      el,
      changeDetectorRef,
      sanitizer,
      studioConfigService,
      componentRefreshService,
      translateService
    );
    this.taxModeSubscription = this.studioTaxService.getTaxMode().subscribe(mode => {
      this.taxMode = mode;
    });
  }

  // getTaxMode() is app-lifetime; left open it keeps this view alive after it closes.
  private taxModeSubscription: Subscription;

  override ngOnDestroy(): void {
    this.taxModeSubscription?.unsubscribe();
    super.ngOnDestroy();
  }

  /** The view renders field_gross_tuition pre-formatted ("$1,100.00"); tax raw ("3.53"). */
  num(v: any): number {
    return parseFloat(String(v ?? '').replace(/[^0-9.\-]/g, '')) || 0;
  }

  /**
   * The customer-facing total: the payment's stored field_total_payment_amount,
   * written mode-correctly at entry (gross + tax for exclusive-tax studios;
   * just gross when the tax was inside the amount handed over: a $676.20
   * inclusive payment is $676.20, not $769.73). Legacy payments predating the
   * field fall back to a computed total gated by the current tax mode.
   */
  paymentTotal(row: any): number {
    const stored = this.num(row?.field_total_payment_amount);
    if (stored > 0) {
      return stored;
    }
    const gross = this.num(row?.field_gross_tuition);
    if (this.taxMode?.hasInclusiveTax && !this.taxMode?.hasExclusiveTax) {
      return gross;
    }
    return gross + this.num(row?.field_tax_collected);
  }

  override getData(params: { parameter: string; value: string; }[]) {
    this.ShowProgressBar = true;
    this.queryParams = params;

    // Add sorting parameters, if available
    if (this.sortParams !== undefined) {
      this.queryParams.push(this.sortParams);
    }

    this._drupalRESTService.httpGET("/api_rest/v1/rest_api_view_payments_on_enrollment/" + this.enrollmentID, params)
      .subscribe((data) => {
        this.ShowProgressBar = false;
        this.data = data;
        this.dataSource = new MatTableDataSource(data['results']);

        // Tax Amount + Total (gross + tax) columns only when any payment carries
        // tax: exclusive-tax studios need to see what the customer actually paid.
        this.hasTaxData = !!data?.['results']?.some((row: any) => this.num(row.field_tax_collected) !== 0);
        const hasCol = (c: string) => this.displayedColumns.includes(c);
        if (this.hasTaxData && !hasCol('field_tax_collected')) {
          const i = this.displayedColumns.indexOf('field_gross_tuition') + 1;
          this.displayedColumns.splice(i, 0, 'field_tax_collected', 'payment_total');
        } else if (!this.hasTaxData && hasCol('field_tax_collected')) {
          this.displayedColumns = this.displayedColumns.filter(c => c !== 'field_tax_collected' && c !== 'payment_total');
        }

        // Pagination
        if (data['results']) {
          this.setupPagination(data, params);
        }

      }, error => this.handleError(error))
  }

  isInteger(value: string): boolean {
    if (value && !isNaN(+value)) {
      return Number(value) % 1 === 0;
    }
    return false;
  }

  override openEntityComponent(component: ComponentType<unknown>, eckType: any, bundle: any, action: any, EntityID?: any, fieldsData?: {}) {
    this._dialogService.openDialog(component, "defaultWithData", {
      data: {
        EntityID: EntityID,
        eckType: eckType,
        bundle: bundle,
        action: action,
        fieldsData: fieldsData ?? '',
      },
    }).afterClosed().subscribe(data => {
      // this.refreshCalendar();
      this.getData(this.queryParams);
    });
  }

}
